import React, { useState, useEffect, useMemo } from 'react'
import DataTable from 'react-data-table-component'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import SponsorForm from './SponsorForm'
import {
  useTableStyles,
  Notice,
  Field,
  StatusPill,
  EmptyState,
  SkeletonList,
  IconPencil,
  IconPlus,
} from './media'
import './media.css'

/*
 * SPONSORS — the list.
 *
 * Same two requests as before (admin/sponsors, admin/sports), same client-side
 * filtering, same showForm(id, open) contract with SponsorForm. What changed is
 * only the surface: panels instead of alerts, a token-themed grid, a real empty
 * state and a skeleton, and filters that stack instead of squashing.
 */

export default function Sponsors() {
  const [form, setForm] = useState(false)
  const [id, setId] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState('')
  const [data, setData] = useState([])
  const [dataFlt, setDataFlt] = useState([])
  const [flt, setFlt] = useState('')
  const [sportFlt, setSportFlt] = useState('')
  const [typeFlt, setTypeFlt] = useState('')
  const [sports, setSports] = useState([])
  const [filtersOpen, setFiltersOpen] = useState(false)

  const tableStyles = useTableStyles()

  /* One cache-buster per load, not per render: a fresh Date.now() on every
     render makes every logo refetch and the list flicker. */
  const stamp = useMemo(() => Date.now(), [data])

  const logoOf = (row) =>
    row && row.UUID
      ? process.env.REACT_APP_API_SERVER + 'admin/files/sponsorlogo/' + row.UUID + '/' + stamp
      : ''

  const columns = [
    {
      name: '',
      cell: (row) => (
        <Btn className="md-icon" onClick={() => { showForm(row.UUID, true) }} aria-label={'Edit — ' + (row.Title || '')}>
          {IconPencil}
        </Btn>
      ),
      width: '58px',
      ignoreRowClick: true,
    },
    {
      name: 'Name',
      selector: (row) => row.Title || '',
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div className="md-cell-title">
          <Avatar src={logoOf(row)} name={row.Title} size={34} />
          <div className="md-cell-name">
            <div className="md-cell-strong">{row.Title}</div>
            <div className="md-cell-sub">{row.IsChallenge == 1 ? 'Challenge' : 'Plain Post'}</div>
          </div>
        </div>
      ),
    },
    {
      name: 'Active',
      selector: (row) => Number(row.IsActive) || 0,
      sortable: true,
      width: '132px',
      cell: (row) => <StatusPill on={row.IsActive} />,
    },
    {
      name: 'Created',
      selector: (row) => row.DateCreated || '',
      sortable: true,
      width: '170px',
      cell: (row) => <span className="md-cell-mono">{row.DateCreated}</span>,
    },
  ]

  useEffect(() => {
    setIsLoading(true)
    loadData()
    loadSports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  const loadData = () => {
    setIsLoading(true)
    setFailed('')
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sponsors', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data.results)
        setDataFlt(res.data.results)
        setIsLoading(false)

      }).catch(err => {
        setIsLoading(false)
        setFailed('The sponsors could not be loaded. Please try again.')
      })
  }

  const loadSports = () => {
    setIsLoading(true)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sports', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSports(res.data.results)
        setIsLoading(false)

      }).catch(err => {
        setIsLoading(false)
        setFailed('The sports could not be loaded.')
      })
  }

  const showForm = (id, a) => {
    setForm(a)
    setId(id)
  }

  const sportsList = sports.map(item => {
    return (
      <option key={item.SportID} value={item.SportID}>{item.SportName}</option>
    )
  })

  const clearCretiria = () => {
    setFlt('')
    setSportFlt('')
    setTypeFlt('')
    setDataFlt([...data])
  }

  const search = (e) => {
    e.preventDefault()
    var dataRes = []
    if (flt.length > 0) {
      dataRes = [...data].filter(item => String(item.Title || '').toUpperCase().includes(flt.toUpperCase()))
    } else {
      dataRes = [...data]
    }

    if (sportFlt.length > 0) {
      dataRes = dataRes.filter(item => item.SportID == sportFlt)
    }

    if (typeFlt.length > 0) {
      dataRes = dataRes.filter(item => item.IsChallenge == typeFlt)
    }

    setDataFlt(dataRes)
  }

  const activeCount = (flt.length > 0 ? 1 : 0) + (sportFlt.length > 0 ? 1 : 0) + (typeFlt.length > 0 ? 1 : 0)

  /* The form owns its own panels — the list steps aside entirely so the two
     never nest a panel inside a panel. */
  if (form) {
    return <SponsorForm id={id} showForm={showForm} setForm={setForm} loadData={loadData} />
  }

  return (
    <div className="md-stack">
      <Panel
        title="Filters and Search"
        subtitle={activeCount > 0 ? activeCount + ' active filters' : 'All sponsors'}
        actions={
          <button
            type="button"
            className="toggle"
            aria-pressed={filtersOpen}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            {filtersOpen ? 'Hide' : 'Show'}
          </button>
        }
      >
        {filtersOpen ? (
          <form className="md-filters" onSubmit={search}>
            <Field label="Title" htmlFor="spf-title">
              <input id="spf-title" className="md-input" type="text" value={flt} onChange={(e) => setFlt(e.target.value)} />
            </Field>

            <Field label="Post Type" htmlFor="spf-type">
              <select id="spf-type" className="md-select" value={typeFlt} onChange={(e) => setTypeFlt(e.target.value)}>
                <option value=''>[Select post type]</option>
                <option value='1'>Challenge</option>
                <option value='0'>Plain Post</option>
              </select>
            </Field>

            <Field label="Sport" htmlFor="spf-sport">
              <select id="spf-sport" className="md-select" value={sportFlt} onChange={(e) => setSportFlt(e.target.value)}>
                <option value=''>[Select sport]</option>
                {sportsList}
              </select>
            </Field>

            <div className="md-actions">
              <Btn onClick={() => clearCretiria()}>Clear</Btn>
              <Btn variant="accent" type="submit">Search</Btn>
            </div>
          </form>
        ) : (
          <div className="md-hint">Search by title, post type and sport.</div>
        )}
      </Panel>

      <Panel
        title="Sponsors List"
        subtitle={isLoading ? 'Loading...' : dataFlt.length + ' records'}
        actions={
          <Btn variant="accent" onClick={() => { showForm(0, true) }}>
            {IconPlus}
            Add
          </Btn>
        }
      >
        {failed ? <Notice kind="crit" onClose={() => setFailed('')}>{failed}</Notice> : null}

        {isLoading ? (
          <SkeletonList rows={6} />
        ) : (
          <div className="md-table-wrap">
            <DataTable
              columns={columns}
              data={dataFlt}
              highlightOnHover
              customStyles={tableStyles}
              noDataComponent={
                <EmptyState
                  title="No sponsors"
                  hint={activeCount > 0 ? 'No results match these filters.' : 'Add the first sponsor.'}
                  action={<Btn variant="accent" onClick={() => { showForm(0, true) }}>{IconPlus}Add</Btn>}
                />
              }
            />
          </div>
        )}
      </Panel>
    </div>
  )
}
